import React, { useRef } from "react";
import { ChevronLeft, ChevronRight, Images } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { PhoneShot } from "./PhoneShot";

/**
 * ScreenshotStrip — horizontally scrollable brutalist gallery of real app
 * screenshots in phone frames. Each slide has a caption label and opens the
 * shared hover lightbox (via PhoneShot zoomable) on desktop.
 */
export const ScreenshotStrip = () => {
  const { t } = useLanguage();
  const trackRef = useRef(null);

  const SHOTS = [
    { id: "painekoe", image: "/assets/img-painekoe.jpeg", k: "painekoe" },
    { id: "liukukello-tiimi", image: "/assets/img-liukukello-tiimi.jpeg", k: "liukukello_tiimi" },
    { id: "pdf-iv", image: "/assets/img-pdf-iv-poytakirja.jpeg", k: "pdf_iv" },
  ];

  const scroll = (dir) => {
    trackRef.current?.scrollBy({ left: dir * 320, behavior: "smooth" });
  };

  return (
    <section id="galleria" data-testid="screenshot-strip" className="relative py-24 md:py-32 bg-black border-t-2 border-[#1a1a1a] grain overflow-hidden">
      {/* Hazard edge accents */}
      <div aria-hidden="true" className="absolute -left-4 top-0 bottom-0 w-3 hazard-stripes-anim opacity-50" />
      <div aria-hidden="true" className="absolute -right-4 top-0 bottom-0 w-3 hazard-stripes-anim opacity-50" />

      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end mb-12">
          <div className="md:col-span-7">
            <div className="inline-flex items-center gap-2 border-2 border-[#FFEA00] bg-black px-3 py-1.5 mb-6">
              <Images className="w-3.5 h-3.5 text-[#FFEA00]" />
              <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#FFEA00]">
                {t("gallery.eyebrow")}
              </span>
            </div>
            <h2 data-testid="gallery-heading" className="font-display font-bold text-white text-4xl md:text-6xl uppercase leading-none tracking-tight">
              {t("gallery.heading_line1")}
              <br />
              <span className="text-[#FFEA00]">{t("gallery.heading_accent")}</span>
            </h2>
          </div>
          <div className="md:col-span-5 flex flex-col gap-6">
            <div className="border-l-2 border-[#FFEA00] pl-4 py-2">
              <p className="text-[#A3A3A3] text-base leading-relaxed">{t("gallery.intro")}</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => scroll(-1)}
                aria-label={t("gallery.prev")}
                data-testid="gallery-prev-btn"
                className="w-12 h-12 flex items-center justify-center border-2 border-[#FFEA00] text-[#FFEA00] bg-transparent hover:bg-[#FFEA00] hover:text-black transition-colors"
              >
                <ChevronLeft className="w-6 h-6 stroke-[2.5]" />
              </button>
              <button
                type="button"
                onClick={() => scroll(1)}
                aria-label={t("gallery.next")}
                data-testid="gallery-next-btn"
                className="w-12 h-12 flex items-center justify-center border-2 border-[#FFEA00] text-[#FFEA00] bg-transparent hover:bg-[#FFEA00] hover:text-black transition-colors"
              >
                <ChevronRight className="w-6 h-6 stroke-[2.5]" />
              </button>
              <span className="ml-2 font-mono text-[10px] tracking-[0.3em] text-[#A3A3A3] uppercase">
                {t("gallery.scroll_hint")}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Scrollable track — bleeds to viewport edge for brutalist strip feel */}
      <div className="relative">
        <div
          ref={trackRef}
          data-testid="gallery-track"
          className="flex gap-6 overflow-x-auto snap-x snap-proximity pb-4 px-5 md:px-10"
          style={{ scrollbarColor: "#FFEA00 #0a0a0a" }}
        >
          {SHOTS.map((s, i) => (
            <figure
              key={s.id}
              data-testid={`gallery-slide-${s.id}`}
              className="shrink-0 w-[240px] md:w-[280px] snap-start m-0"
            >
              <PhoneShot
                src={s.image}
                alt={t(`gallery.shots.${s.k}`)}
                testId={`gallery-shot-${s.id}`}
                focus="top"
                aspect="portrait"
                zoomable
              />
              <figcaption className="mt-3 flex items-baseline gap-2">
                <span className="font-display font-bold text-[#FFEA00] text-sm leading-none">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="font-mono text-[10px] md:text-[11px] tracking-[0.2em] uppercase text-[#A3A3A3]">
                  {t(`gallery.shots.${s.k}`)}
                </span>
              </figcaption>
            </figure>
          ))}
        </div>

        {/* Right edge fade — hints at more content */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute top-0 right-0 bottom-4 w-16 md:w-28"
          style={{ background: "linear-gradient(90deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.85) 100%)" }}
        />
      </div>
    </section>
  );
};
